import { WorkSpaceNode } from 'stores/WorkSpaceStoreAdapter';

import { EditableAnswerCode } from './AnswerCodesEditor';

/**
 * 編集フォームの正解コード・期待値を knz の AnswerCode / Expected ブロックにシリアライズする。
 *
 * knz スキーマ（議題6 / D6-B・D6-B3）:
 * - AnswerCode: `file`（正解コードの絶対パス）＋ `expected`（Expected の id リスト `- id`）
 * - Expected: `id` ＋ `sourceFile`（由来のファイルパス）＋ `sourceSeq`（由来連番）のみ（stdout/arguments は書かない）
 * - AnswerCode / Expected は記事本文には描画されない（設問プレフィル用メタデータ）
 *
 * knz には可読性のためファイルパス（例: /src/Main.java）を書く。
 */

/** fileId → 絶対パス の逆引きマップをツリーから作る。 */
export const buildFileIdToPath = (nodes: WorkSpaceNode[]): Map<string, string> => {
  const map = new Map<string, string>();
  nodes.forEach((n) => {
    if (n.type === 'FILE' && n.fileId && n.path) map.set(n.fileId, n.path);
  });
  return map;
};

/** 正解コードの参照文字列（パス優先・なければ fileId フォールバック）。 */
const refOf = (ac: EditableAnswerCode, idToPath?: Map<string, string>): string =>
  ac.filePath || idToPath?.get(ac.fileId) || ac.fileId;

const serializeAnswerBlocks = (
  answerCodes: EditableAnswerCode[],
  idToPath?: Map<string, string>,
): string[] => {
  const lines: string[] = [];
  answerCodes.forEach((ac, aIdx) => {
    const ref = refOf(ac, idToPath);
    const expectedIds = ac.expectedList.map((_, eIdx) => `ac${aIdx}-e${eIdx}`);
    // AnswerCode: file ＋ expected（id リスト）
    lines.push('<--- AnswerCode --->');
    lines.push(`file:${ref}`);
    if (expectedIds.length > 0) {
      lines.push('expected:');
      expectedIds.forEach((id) => lines.push(` - ${id}`));
    }
    lines.push('<--- !AnswerCode --->');
    lines.push('');
    // Expected: id ＋ sourceFile ＋ sourceSeq（本文には描画されない）
    ac.expectedList.forEach((ex, eIdx) => {
      lines.push('<--- Expected --->');
      lines.push(`id:ac${aIdx}-e${eIdx}`);
      // 期待値の由来は正解コードと同じファイル（実行履歴）。パスで書く。
      lines.push(`sourceFile:${ref}`);
      lines.push(`sourceSeq:${ex.sourceSeq}`);
      lines.push('<--- !Expected --->');
      lines.push('');
    });
  });
  return lines;
};

/**
 * 元の knz ソースから既存の AnswerCode / Expected ブロックを除去し、末尾に新しい検証ブロックを挿入する。
 * 設問本文（frontmatter / Section / Text 等）はそのまま保持する。
 * @param idToPath fileId → 絶対パス のマップ（filePath が未取得の正解コードのフォールバック）
 */
export const rewriteAnswerBlocksInKnz = (
  source: string,
  answerCodes: EditableAnswerCode[],
  idToPath?: Map<string, string>,
): string => {
  const lines = source.split('\n');
  const kept: string[] = [];
  let skipping: 'AnswerCode' | 'Expected' | null = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (skipping) {
      if (trimmed === `<--- !${skipping} --->`) {
        skipping = null;
      }
      continue;
    }
    if (trimmed === '<--- AnswerCode --->') {
      skipping = 'AnswerCode';
      continue;
    }
    if (trimmed === '<--- Expected --->') {
      skipping = 'Expected';
      continue;
    }
    kept.push(line);
  }

  // 末尾の余分な空行を1つに整える
  while (kept.length > 0 && kept[kept.length - 1].trim() === '') {
    kept.pop();
  }

  const blocks = serializeAnswerBlocks(answerCodes, idToPath);
  return [...kept, '', ...blocks].join('\n');
};
